import { create } from "zustand";
import type { Column, GridPayload } from "../types";
import type { RowData, Table } from "@tanstack/react-table";

type KeyOrString<T> = keyof T | (string & {});

type TanstackTableInstance = {
  getAllColumns(): any[];
  getVisibleLeafColumns(): any[];
};

type UseGridSettingsStoreActions = {
  setAvailableColumns: (cols: Column[]) => void;
  setCurrentColumns: (cols: Column[]) => void;
  setPayload: (payload: GridPayload) => void;
  reset: () => void;
  extractColumns: (table: TanstackTableInstance) => {
    availableColumns: Column[];
    currentColumns: Column[];
  };
  syncColumnsState: <TData = RowData>(
    table: Table<TData>,
    options?: {
      excludeColumns: KeyOrString<TData>[];
      labelMap?: Partial<Record<KeyOrString<TData>, string>>;
      visibility?: Record<keyof TData, boolean>;
    }
  ) => void;
};

type UseGridSettingsStoreState = {
  availableColumns: Column[];
  currentColumns: Column[];
  payload: GridPayload;
};

const useGridSettingsStore = create<UseGridSettingsStoreState & UseGridSettingsStoreActions>(
  (set, get) => ({
    availableColumns: [],
    currentColumns: [],
    payload: {
      ordering: [],
      visibility: {},
      pinning: { left: [] }
    },
    setAvailableColumns: (cols: Column[]) => set({ availableColumns: cols }),
    setCurrentColumns: (cols: Column[]) => set({ currentColumns: cols }),
    setPayload: (payload: GridPayload) => set({ payload }),
    reset: () =>
      set({
        availableColumns: [],
        currentColumns: [],
        payload: { ordering: [], visibility: {}, pinning: { left: [] } }
      }),
    extractColumns: (table: TanstackTableInstance) => {
      const availableColumns = table.getAllColumns().map((col) => ({ id: col.id }));
      const currentColumns = table.getVisibleLeafColumns().map((col) => ({ id: col.id }));
      return { availableColumns, currentColumns };
    },
    syncColumnsState: <TData = RowData>(
      table: Table<TData>,
      options?: {
        excludeColumns?: KeyOrString<TData>[];
        labelMap?: Partial<Record<KeyOrString<TData>, string>>;
        visibility?: Record<keyof TData, boolean>;
      }
    ) => {
      const { availableColumns, currentColumns } = get().extractColumns(table);
      const { excludeColumns = [], labelMap = {}, visibility = {} } = options || {};

      const filteredExcludeColumnFn = (column: Column) => !excludeColumns.includes(column.id as any);

      const mapLabel = (col: Column) => ({
        id: col.id,
        label: labelMap?.[col.id as keyof typeof labelMap] ?? col.label ?? col.id
      });

      set({
        availableColumns: availableColumns.filter(filteredExcludeColumnFn).map(mapLabel),
        currentColumns: currentColumns
          .filter(filteredExcludeColumnFn)
          .filter((column) => {
            for (const [columnId, isVisible] of Object.entries(visibility)) {
              if (column.id === columnId && !isVisible) {
                return false;
              }
            }
            return true;
          })
          .map(mapLabel)
      });
    }
  })
);

export default useGridSettingsStore;
